import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { warehouseAPI } from '../../api/warehouses';
import { Sparkles, Save, Trash2, Power, PowerOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { AutoCalculateModal } from './AutoCalculateModal';

interface ThresholdData {
  warehouseId: number;
  itemId: number;
  itemName: string;
  minStockLevel: number | null;
  maxStockLevel: number | null;
  alertEnabled: boolean;
  currentStock: number;
  unit: string;
}

export const ThresholdConfigPanel: React.FC = () => {
  const { t } = useTranslation();
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [thresholds, setThresholds] = useState<ThresholdData[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<ThresholdData>>({});
  const [showAutoCalculate, setShowAutoCalculate] = useState(false);

  const loadWarehouses = async () => {
    try {
      const result = await warehouseAPI.getAll();
      setWarehouses(result);
      if (result.length > 0 && !selectedWarehouseId) {
        setSelectedWarehouseId(result[0].id);
      }
    } catch (error) {
      console.error('Failed to load warehouses:', error);
      toast.error(t('common.error'));
    }
  };

  const loadThresholds = async () => {
    if (!selectedWarehouseId) return;

    try {
      setLoading(true);
      const result = await warehouseAPI.getThresholds(selectedWarehouseId);
      setThresholds(result);
    } catch (error) {
      console.error('Failed to load thresholds:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWarehouses();
  }, []);

  useEffect(() => {
    if (selectedWarehouseId) {
      loadThresholds();
    }
  }, [selectedWarehouseId]);

  const handleEdit = (threshold: ThresholdData) => {
    setEditingRow(threshold.itemId);
    setEditData({
      minStockLevel: threshold.minStockLevel,
      maxStockLevel: threshold.maxStockLevel,
      alertEnabled: threshold.alertEnabled,
    });
  };

  const handleSave = async (itemId: number) => {
    if (!selectedWarehouseId) return;

    try {
      await warehouseAPI.upsertThreshold(selectedWarehouseId, itemId, {
        minStockLevel: editData.minStockLevel || undefined,
        maxStockLevel: editData.maxStockLevel || undefined,
        alertEnabled: editData.alertEnabled ?? true,
      });
      toast.success(t('common.saved'));
      setEditingRow(null);
      setEditData({});
      loadThresholds();
    } catch (error) {
      console.error('Failed to save threshold:', error);
      toast.error(t('common.error'));
    }
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditData({});
  };

  const handleDelete = async (itemId: number) => {
    if (!selectedWarehouseId) return;

    if (!window.confirm(t('common.confirmDelete'))) return;

    try {
      await warehouseAPI.deleteThreshold(selectedWarehouseId, itemId);
      toast.success(t('common.deleted'));
      loadThresholds();
    } catch (error) {
      console.error('Failed to delete threshold:', error);
      toast.error(t('common.error'));
    }
  };

  const handleToggleEnabled = async (itemId: number, currentEnabled: boolean) => {
    if (!selectedWarehouseId) return;

    const threshold = thresholds.find((t) => t.itemId === itemId);
    if (!threshold) return;

    try {
      await warehouseAPI.upsertThreshold(selectedWarehouseId, itemId, {
        minStockLevel: threshold.minStockLevel || undefined,
        maxStockLevel: threshold.maxStockLevel || undefined,
        alertEnabled: !currentEnabled,
      });
      toast.success(t('common.saved'));
      loadThresholds();
    } catch (error) {
      console.error('Failed to toggle threshold:', error);
      toast.error(t('common.error'));
    }
  };

  if (loading && !thresholds.length) {
    return (
      <div className="rounded-lg border bg-white p-6">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 rounded bg-gray-200"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Warehouse Selector + Auto-Calculate Button */}
      <div className="flex items-center justify-between rounded-lg border bg-white p-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700">
            {t('warehouse.warehouse')}
          </label>
          <select
            value={selectedWarehouseId || ''}
            onChange={(e) => setSelectedWarehouseId(parseInt(e.target.value))}
            className="mt-1 block w-full max-w-md rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
          >
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={() => setShowAutoCalculate(true)}
          disabled={!selectedWarehouseId}
          className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:bg-gray-300"
        >
          <Sparkles className="h-5 w-5" />
          {t('warehouse.stockAlerts.autoCalculate')}
        </button>
      </div>

      {/* Thresholds Table */}
      <div className="overflow-hidden rounded-lg border bg-white shadow">
        {thresholds.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-600">{t('warehouse.stockAlerts.noThresholds')}</p>
            <button
              onClick={() => setShowAutoCalculate(true)}
              className="mt-4 flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
            >
              <Sparkles className="h-5 w-5" />
              {t('warehouse.stockAlerts.autoCalculate')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('item.item')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('warehouse.stockAlerts.currentStock')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('warehouse.stockAlerts.minThreshold')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('warehouse.stockAlerts.maxThreshold')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('common.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {thresholds.map((threshold) => (
                  <tr key={threshold.itemId} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {threshold.itemName}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {threshold.currentStock.toFixed(2)} {threshold.unit}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      {editingRow === threshold.itemId ? (
                        <input
                          type="number"
                          step="0.001"
                          value={editData.minStockLevel ?? ''}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              minStockLevel: e.target.value ? parseFloat(e.target.value) : null,
                            })
                          }
                          className="w-24 rounded border border-gray-300 px-2 py-1"
                        />
                      ) : (
                        <span>{threshold.minStockLevel?.toFixed(2) || '-'}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      {editingRow === threshold.itemId ? (
                        <input
                          type="number"
                          step="0.001"
                          value={editData.maxStockLevel ?? ''}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              maxStockLevel: e.target.value ? parseFloat(e.target.value) : null,
                            })
                          }
                          className="w-24 rounded border border-gray-300 px-2 py-1"
                        />
                      ) : (
                        <span>{threshold.maxStockLevel?.toFixed(2) || '-'}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <button
                        onClick={() => handleToggleEnabled(threshold.itemId, threshold.alertEnabled)}
                        className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${
                          threshold.alertEnabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {threshold.alertEnabled ? (
                          <>
                            <Power className="h-3 w-3" />
                            {t('warehouse.stockAlerts.enabled')}
                          </>
                        ) : (
                          <>
                            <PowerOff className="h-3 w-3" />
                            {t('warehouse.stockAlerts.disabled')}
                          </>
                        )}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      {editingRow === threshold.itemId ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSave(threshold.itemId)}
                            className="rounded bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700"
                          >
                            {t('common.save')}
                          </button>
                          <button
                            onClick={handleCancel}
                            className="rounded bg-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-400"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(threshold)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Save className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(threshold.itemId)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Auto-Calculate Modal */}
      {showAutoCalculate && selectedWarehouseId && (
        <AutoCalculateModal
          warehouseId={selectedWarehouseId}
          onClose={() => setShowAutoCalculate(false)}
          onApplied={() => {
            setShowAutoCalculate(false);
            loadThresholds();
          }}
        />
      )}
    </div>
  );
};
